import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Check, Clock, Mail, Phone, RotateCcw, Trash2, X } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { AuthImage } from "@/components/owner/AuthImage";
import { HouseDialog } from "@/components/site/HouseDialog";

const FILTERS = ["pending", "approved", "declined", "all"];

const BookingCard = ({ bk, onStatus, onDelete, onZoom, onDeposit, onRefund, onVipStamp }) => (
  <article className="owner-card bk-card" data-status={bk.status} data-testid={`owner-booking-${bk.id}`}>
    <div className="bk-top">
      <div>
        <p className="bk-when">{format(parseISO(bk.date), "EEE, MMM d")} · {bk.time_label}{bk.confirmed ? " · ✓ confirmed" : ""}</p>
        <h3>{bk.name}</h3>
      </div>
      <span className="status-pill" data-status={bk.deposit_refunded ? "declined" : bk.deposit_paid ? "approved" : bk.status}
        data-testid={`owner-deposit-${bk.id}`}>
        {bk.deposit_refunded ? "$15 deposit refunded" : bk.deposit_paid ? `$15 deposit paid${bk.deposit_method === "manual" ? " · recorded" : ""}` : "$15 deposit due"}
      </span>
    </div>
    <p className="bk-service">{bk.service}{bk.lux_style ? ` — ${bk.lux_style}` : ""}</p>
    <p className="bk-meta">{bk.appt_type}</p>
    <p className="bk-meta"><a href={`mailto:${bk.email}`}><Mail size={14} /> {bk.email}</a> <a href={`tel:${bk.phone}`}><Phone size={14} /> {bk.phone}</a></p>
    {bk.notes ? <p className="bk-notes">"{bk.notes}"</p> : null}
    <p className="bk-meta"><Clock size={14} /> Runs about {bk.duration_hours} hr{bk.duration_hours > 1 ? "s" : ""} · {bk.photo_count} hair photo{bk.photo_count === 1 ? "" : "s"}</p>
    <div className="bk-photos">
      {Array.from({ length: bk.photo_count }, (_, i) => (
        <AuthImage key={i} path={`/admin/bookings/${bk.id}/photos/${i}`} alt={`Hair photo ${i + 1}`} className="bk-photo"
          onClick={onZoom} testId={`owner-booking-photo-${bk.id}-${i}`} />
      ))}
    </div>
    <div className="bk-actions">
      {bk.status !== "approved" ? <button type="button" className="cta sm" onClick={() => onStatus(bk, "approved")}
        data-testid={`owner-approve-${bk.id}`}><Check size={15} /> Approve</button> : null}
      {bk.status !== "declined" ? <button type="button" className="cta ghost sm" onClick={() => onStatus(bk, "declined")}
        data-testid={`owner-decline-${bk.id}`}><X size={15} /> Decline</button> : null}
      {bk.status === "approved" && !bk.deposit_paid && !bk.deposit_refunded ? (
        <button type="button" className="cta ghost sm" onClick={() => onDeposit(bk, true)}
          data-testid={`owner-mark-paid-${bk.id}`}><Check size={15} /> Record $15 paid</button>
      ) : null}
      {bk.status === "approved" ? (bk.vip_stamped ? (
        <span className="status-pill" data-status="approved" data-testid={`vip-stamped-${bk.id}`}>♛ VIP stamped</span>
      ) : (
        <button type="button" className="cta ghost sm" onClick={() => onVipStamp(bk)}
          title="Give her gold bunny stamp for this completed visit"
          data-testid={`owner-vip-stamp-${bk.id}`}>♛ VIP Stamp</button>
      )) : null}
      {bk.deposit_paid ? (
        <button type="button" className="cta ghost sm" onClick={() => onRefund(bk)}
          data-testid={`owner-refund-${bk.id}`}><RotateCcw size={15} /> Refund deposit</button>
      ) : null}
      <button type="button" className="icon-btn" onClick={() => onDelete(bk)} aria-label="Delete request"
        data-testid={`owner-delete-${bk.id}`}><Trash2 size={16} /></button>
    </div>
  </article>
);

export const BookingsTab = ({ onAuthFail }) => {
  const [filter, setFilter] = useState("pending");
  const [data, setData] = useState({ bookings: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings", { params: filter === "all" ? {} : { status: filter } });
      setData({ bookings: Array.isArray(r.data?.bookings) ? r.data.bookings : [], counts: r.data?.counts || {} });
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    } finally { setLoading(false); }
  }, [filter, onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (bk, status) => {
    try { await api.patch(`/admin/bookings/${bk.id}`, { status }); toast.success(`Request ${status} — ${bk.name} was emailed.`); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const onDeposit = async (bk) => {
    if (!window.confirm(`Record ${bk.name}'s $15 deposit as paid (Apple Pay, Cash App, Venmo or cash)?`)) return;
    try { await api.patch(`/admin/bookings/${bk.id}/deposit`, { paid: true }); toast.success("Deposit recorded as paid."); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const onRefund = async (bk) => {
    if (!window.confirm(`Refund ${bk.name}'s $15 deposit? Card deposits go back through Stripe automatically.`)) return;
    try { await api.post(`/admin/bookings/${bk.id}/refund`); toast.success("Deposit refunded — the client was emailed."); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const onVipStamp = async (bk) => {
    try {
      const r = await api.post(`/admin/bookings/${bk.id}/vip-stamp`);
      toast.success(`${bk.name} earned a gold bunny stamp ♛${r.data.issued.length ? ` 🎉 ${r.data.issued.map((k) => `$${k.value} Kash issued!`).join(" ")}` : ""}${r.data.created ? ` She's a new VIP — Black Card + access code emailed (${r.data.code}).` : ""}`);
      load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const onDelete = async (bk) => {
    if (!window.confirm(`Delete ${bk.name}'s request permanently?`)) return;
    try { await api.delete(`/admin/bookings/${bk.id}`); toast.success("Request deleted."); load(); }
    catch (e) { toast.error(errorText(e)); }
  };

  return (
    <section>
      <div className="owner-filters">
        {FILTERS.map((f) => (
          <button key={f} type="button" data-active={filter === f} onClick={() => setFilter(f)} data-testid={`owner-filter-${f}`}>
            {f}{f !== "all" ? <span>{data.counts[f] ?? 0}</span> : null}
          </button>
        ))}
      </div>
      {loading ? <p className="owner-empty">Loading…</p> : data.bookings.length === 0 ? (
        <p className="owner-empty" data-testid="owner-bookings-empty">No {filter === "all" ? "" : filter} requests right now.</p>
      ) : (
        <div className="bk-grid">
          {data.bookings.map((bk) => <BookingCard key={bk.id} bk={bk} onStatus={onStatus} onDelete={onDelete}
            onZoom={setZoom} onDeposit={onDeposit} onRefund={onRefund} onVipStamp={onVipStamp} />)}
        </div>
      )}
      <HouseDialog open={!!zoom} onOpenChange={() => setZoom("")} title="Hair photo" testId="owner-photo-zoom">
        {zoom ? <img src={zoom} alt="Hair photo" className="lightbox-img" /> : null}
      </HouseDialog>
    </section>
  );
};
